import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import SelectorCategoriaMoodle from "./SelectorCategoriaMoodle";
import Swal from "sweetalert2";

/* ============================================================
   HELPERS
   ============================================================ */

const normalizar = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

const generarClaveBase = (nombreMateria = "") =>
  normalizar(nombreMateria)
    .replace(/[^A-Z0-9]/g, "")
    .substring(0, 2);

const generarInicialesCategoria = (nombreCategoria = "") => {
  const textoNormalizado = normalizar(nombreCategoria)
    .replace(/[^A-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!textoNormalizado) {
    return "CAT";
  }

  const palabras = textoNormalizado
    .split(" ")
    .filter(Boolean);

  let iniciales = palabras
    .map((palabra) => palabra.charAt(0))
    .join("")
    .substring(0, 3);

  const nombreSinEspacios =
    textoNormalizado.replace(/\s+/g, "");

  for (
    let indice = 0;
    iniciales.length < 3 &&
    indice < nombreSinEspacios.length;
    indice += 1
  ) {
    const letra = nombreSinEspacios[indice];

    if (!iniciales.includes(letra)) {
      iniciales += letra;
    }
  }

  return iniciales
    .padEnd(3, "X")
    .substring(0, 3);
};

const fechaAMoodleTimestamp = (fecha) => {
  if (!fecha) return null;

  const fechaConvertida =
    new Date(`${fecha}T00:00:00`);

  if (
    Number.isNaN(
      fechaConvertida.getTime()
    )
  ) {
    return null;
  }

  return Math.floor(
    fechaConvertida.getTime() / 1000
  );
};

const escaparHTML = (valor) =>
  String(valor ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatearFecha = (timestamp) => {
  if (!timestamp) {
    return "Sin fecha";
  }

  try {
    return new Date(
      timestamp * 1000
    ).toLocaleDateString(
      "es-AR",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }
    );
  } catch {
    return "Fecha inválida";
  }
};

const obtenerMensajeBackend = (datos) => {
  if (!datos) {
    return "El backend no devolvió un mensaje.";
  }

  return (
    datos.mensaje ||
    datos.message ||
    datos.resultado ||
    datos.descripcion ||
    datos.error ||
    "Proceso completado."
  );
};

/* ============================================================
   COMPONENTE
   ============================================================ */

function SelectorCursoMateria({
  Cursos = [],
}) {
  const cursosValidos = useMemo(
    () =>
      Array.isArray(Cursos)
        ? Cursos.filter(Boolean)
        : [],
    [Cursos]
  );

  /* ==========================================================
     ESTADOS GENERALES
     ========================================================== */

  const [
    cursoSeleccionado,
    setCursoSeleccionado,
  ] = useState(null);

  const [
    materias,
    setMaterias,
  ] = useState([]);

  const [
    seleccionadas,
    setSeleccionadas,
  ] = useState({});

  const [
    categoriaFinal,
    setCategoriaFinal,
  ] = useState(null);

  const [
    shortnamesEditados,
    setShortnamesEditados,
  ] = useState({});

  const [
    editandoUid,
    setEditandoUid,
  ] = useState(null);

  const [
    cargandoMaterias,
    setCargandoMaterias,
  ] = useState(false);

  const [
    generando,
    setGenerando,
  ] = useState(false);

  const [
    ultimoResultado,
    setUltimoResultado,
  ] = useState(null);

  /* ==========================================================
     NUEVOS ESTADOS DE VERIFICACIÓN MOODLE
     ========================================================== */

  const [
    verificandoMoodle,
    setVerificandoMoodle,
  ] = useState(false);

  const [
    moodleVerificado,
    setMoodleVerificado,
  ] = useState(false);

  /*
   * Estructura:
   *
   * {
   *   uid: {
   *     existe: true/false,
   *     estado: "existente" | "nuevo" | "error",
   *     idCursoMoodle: 123
   *   }
   * }
   */

  const [
    estadoMoodle,
    setEstadoMoodle,
  ] = useState({});

  const inicializadoRef =
    useRef(false);

  /* ==========================================================
     RESETEAR VERIFICACIÓN
     ========================================================== */

  const resetearVerificacionMoodle =
    () => {
      setEstadoMoodle({});
      setMoodleVerificado(false);
    };

  /* ==========================================================
     TRAER MATERIAS DEL CURSO
     ========================================================== */

  useEffect(() => {
    if (!cursoSeleccionado) {
      setMaterias([]);
      setSeleccionadas({});
      setShortnamesEditados({});
      setUltimoResultado(null);

      resetearVerificacionMoodle();

      inicializadoRef.current =
        false;

      return;
    }

    const controller =
      new AbortController();

    const fetchMateriasCurso =
      async () => {
        setCargandoMaterias(true);

        setMaterias([]);
        setSeleccionadas({});
        setShortnamesEditados({});
        setUltimoResultado(null);

        resetearVerificacionMoodle();

        inicializadoRef.current =
          false;

        try {
          const response =
            await axios.post(
              "https://wwwa.isepsantafe.net.ar/expedientes/materias/devuelveMateriasPorCursoDocentes",
              {
                curso:
                  cursoSeleccionado.nombreCurso,
              },
              {
                signal:
                  controller.signal,
              }
            );

          const materiasRecibidas =
            Array.isArray(
              response.data?.materias
            )
              ? response.data.materias
              : [];

          setMaterias(
            materiasRecibidas
          );
        } catch (error) {
          if (
            !axios.isCancel(error)
          ) {
            console.error(
              "Error al obtener materias:",
              error
            );

            setMaterias([]);

            Swal.fire({
              icon: "error",
              title:
                "No se pudieron cargar las materias",

              text:
                error?.response?.data
                  ?.mensaje ||
                error?.response?.data
                  ?.error ||
                error?.message ||
                "Ocurrió un problema al consultar las materias del curso.",
            });
          }
        } finally {
          if (
            !controller.signal
              .aborted
          ) {
            setCargandoMaterias(
              false
            );
          }
        }
      };

    fetchMateriasCurso();

    return () =>
      controller.abort();
  }, [cursoSeleccionado]);

  /* ==========================================================
     ESTRUCTURA DE VISTA
     ========================================================== */

  const estructuraVista =
    useMemo(() => {
      if (
        !cursoSeleccionado ||
        materias.length === 0
      ) {
        return null;
      }

      const nombreCategoria =
        categoriaFinal?.nombre ||
        categoriaFinal?.name ||
        "";

      const inicialesCategoria =
        generarInicialesCategoria(
          nombreCategoria
        );

      const cantidadDivisiones =
        Number(
          cursoSeleccionado.cantDivisiones
        ) > 0
          ? Number(
              cursoSeleccionado.cantDivisiones
            )
          : 1;

      return Array.from(
        {
          length:
            cantidadDivisiones,
        },
        (_, idx) => {
          const division =
            idx + 1;

          const contadorPorNombre =
            {};

          return {
            division,

            materias:
              materias.map(
                (materia) => {
                  const baseGenerada =
                    generarClaveBase(
                      materia.nombreMateria
                    ) || "MA";

                  contadorPorNombre[
                    baseGenerada
                  ] =
                    (contadorPorNombre[
                      baseGenerada
                    ] || 0) + 1;

                  const cantidadMismaInicial =
                    contadorPorNombre[
                      baseGenerada
                    ];

                  const sufijoMateria =
                    cantidadMismaInicial >
                    1
                      ? String(
                          cantidadMismaInicial
                        )
                      : "";

                  const uid =
                    `${materia.id}_${division}_${cantidadMismaInicial}`;

                  /*
                   * Ejemplo:
                   *
                   * MA_DIV01_123_ESP
                   */

                  const shortnameBase =
                    `${baseGenerada}${sufijoMateria}` +
                    `_DIV${String(
                      division
                    ).padStart(
                      2,
                      "0"
                    )}` +
                    `_${cursoSeleccionado.id}` +
                    `_${inicialesCategoria}`;

                  const shortname =
                    shortnamesEditados[
                      uid
                    ] ??
                    shortnameBase;

                  return {
                    uid,

                    idMateria:
                      materia.id,

                    nombreMateria:
                      materia.nombreMateria,

                    nombreCompleto:
                      `${
                        materia.nombreMateria
                      } - División ${String(
                        division
                      ).padStart(
                        2,
                        "0"
                      )}`,

                    shortname,

                    division,

                    idCurso:
                      cursoSeleccionado.id,

                    nombreCurso:
                      cursoSeleccionado.nombreCurso,

                    startdate:
                      fechaAMoodleTimestamp(
                        materia.fechaInicio
                      ),

                    enddate:
                      fechaAMoodleTimestamp(
                        materia.fechaFinalizacion
                      ),
                  };
                }
              ),
          };
        }
      );
    }, [
      cursoSeleccionado,
      materias,
      shortnamesEditados,
      categoriaFinal,
    ]);

  /* ==========================================================
     INICIALIZAR SELECCIÓN
     
     Antes de verificar:
     NO seleccionamos nada automáticamente.
     
     Después de verificar:
     seleccionamos solamente las NUEVAS.
     ========================================================== */

  useEffect(() => {
    if (
      !estructuraVista ||
      inicializadoRef.current
    ) {
      return;
    }

    const inicial = {};

    estructuraVista.forEach(
      (division) => {
        division.materias.forEach(
          (materia) => {
            inicial[
              materia.uid
            ] = false;
          }
        );
      }
    );

    setSeleccionadas(inicial);

    inicializadoRef.current =
      true;
  }, [estructuraVista]);

  /* ==========================================================
     TODAS LAS AULAS DE LA ESTRUCTURA
     ========================================================== */

  const todasLasAulas =
    useMemo(() => {
      if (!estructuraVista) {
        return [];
      }

      return estructuraVista.flatMap(
        (division) =>
          division.materias.map(
            (materia) => ({
              ...materia,

              shortname:
                shortnamesEditados[
                  materia.uid
                ] ??
                materia.shortname,
            })
          )
      );
    }, [
      estructuraVista,
      shortnamesEditados,
    ]);

  /* ==========================================================
     MATERIAS SELECCIONADAS
     ========================================================== */

  const materiasSeleccionadas =
    useMemo(() => {
      if (!estructuraVista) {
        return [];
      }

      return estructuraVista.flatMap(
        (division) =>
          division.materias
            .filter(
              (materia) =>
                seleccionadas[
                  materia.uid
                ]
            )
            .map(
              (materia) => ({
                ...materia,

                shortname:
                  shortnamesEditados[
                    materia.uid
                  ] ??
                  materia.shortname,
              })
            )
      );
    }, [
      estructuraVista,
      seleccionadas,
      shortnamesEditados,
    ]);

  /* ==========================================================
     RESUMEN VERIFICACIÓN
     ========================================================== */

  const resumenMoodle =
    useMemo(() => {
      const estados =
        Object.values(
          estadoMoodle
        );

      return {
        total:
          estados.length,

        existentes:
          estados.filter(
            (item) =>
              item?.existe === true
          ).length,

        nuevos:
          estados.filter(
            (item) =>
              item?.existe ===
                false &&
              item?.estado !==
                "error"
          ).length,

        errores:
          estados.filter(
            (item) =>
              item?.estado ===
              "error"
          ).length,
      };
    }, [estadoMoodle]);

  /* ==========================================================
     VERIFICAR AULAS EN MOODLE
     ========================================================== */

  const verificarAulasMoodle =
    async () => {
      if (
        !cursoSeleccionado
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "Seleccioná un curso",
        });

        return;
      }

      if (
        !categoriaFinal?.id
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "Seleccioná una categoría",
          text:
            "Debés elegir primero la categoría padre de Moodle.",
        });

        return;
      }

      if (
        todasLasAulas.length ===
        0
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "No hay aulas para verificar",
        });

        return;
      }

      setVerificandoMoodle(
        true
      );

      setMoodleVerificado(
        false
      );

      setEstadoMoodle({});

      setSeleccionadas({});

      Swal.fire({
        title:
          "Verificando Moodle",

        html: `
          <p>
            Se están consultando las aulas existentes.
          </p>

          <p>
            <strong>
              Todavía no se creará ni modificará nada.
            </strong>
          </p>
        `,

        allowOutsideClick:
          false,

        allowEscapeKey:
          false,

        showConfirmButton:
          false,

        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const respuesta =
          await axios.post(
            "https://wwwa.isepsantafe.net.ar/expedientes/verificarEstructuraMoodle",
            {
              idCurso:
                cursoSeleccionado.id,

              estructura:
                todasLasAulas,
            }
          );

        const resultados =
          Array.isArray(
            respuesta.data
              ?.resultados
          )
            ? respuesta.data
                .resultados
            : [];

        const mapaEstado =
          {};

        const nuevasSeleccionadas =
          {};

        resultados.forEach(
          (item) => {
            if (!item?.uid) {
              return;
            }

            mapaEstado[
              item.uid
            ] = item;

            /*
             * SOLAMENTE las aulas
             * NUEVAS quedan seleccionadas.
             */

            nuevasSeleccionadas[
              item.uid
            ] =
              item.existe ===
                false &&
              item.estado !==
                "error";
          }
        );

        /*
         * Por seguridad dejamos
         * en false cualquier aula
         * que el backend no haya
         * devuelto.
         */

        todasLasAulas.forEach(
          (materia) => {
            if (
              nuevasSeleccionadas[
                materia.uid
              ] === undefined
            ) {
              nuevasSeleccionadas[
                materia.uid
              ] = false;
            }
          }
        );

        setEstadoMoodle(
          mapaEstado
        );

        setSeleccionadas(
          nuevasSeleccionadas
        );

        setMoodleVerificado(
          true
        );

        Swal.close();

        const existentes =
          resultados.filter(
            (item) =>
              item.existe === true
          ).length;

        const nuevos =
          resultados.filter(
            (item) =>
              item.existe ===
                false &&
              item.estado !==
                "error"
          ).length;

        const errores =
          resultados.filter(
            (item) =>
              item.estado ===
              "error"
          ).length;

        await Swal.fire({
          icon:
            errores > 0
              ? "warning"
              : "success",

          title:
            "Verificación terminada",

          html: `
            <div style="
              text-align:left;
              font-size:16px;
            ">

              <p>
                <strong>
                  Resultado de Moodle:
                </strong>
              </p>

              <div style="
                padding:12px;
                border:1px solid #dee2e6;
                border-radius:8px;
                background:#f8f9fa;
              ">

                <div style="margin-bottom:8px;">
                  ♻️
                  <strong>Ya existentes:</strong>
                  ${existentes}
                </div>

                <div style="margin-bottom:8px;">
                  🆕
                  <strong>Nuevas:</strong>
                  ${nuevos}
                </div>

                <div>
                  ❌
                  <strong>Errores:</strong>
                  ${errores}
                </div>

              </div>

              ${
                nuevos > 0
                  ? `
                    <div style="
                      margin-top:15px;
                      padding:10px;
                      border-left:4px solid #198754;
                      background:#d1e7dd;
                    ">
                      Se seleccionaron automáticamente
                      <strong>${nuevos}</strong>
                      aulas nuevas.
                    </div>
                  `
                  : `
                    <div style="
                      margin-top:15px;
                      padding:10px;
                      border-left:4px solid #0d6efd;
                      background:#e7f1ff;
                    ">
                      No hay aulas nuevas para crear.
                    </div>
                  `
              }

              <p style="
                margin-top:15px;
                margin-bottom:0;
              ">
                Las aulas existentes
                <strong>
                  no serán modificadas.
                </strong>
              </p>

            </div>
          `,
        });
      } catch (error) {
        console.error(
          "Error verificando Moodle:",
          error
        );

        Swal.close();

        setEstadoMoodle({});
        setSeleccionadas({});
        setMoodleVerificado(
          false
        );

        Swal.fire({
          icon: "error",

          title:
            "No se pudo verificar Moodle",

          text:
            error?.response?.data
              ?.mensaje ||
            error?.response?.data
              ?.error ||
            error?.message ||
            "Ocurrió un error verificando las aulas.",
        });
      } finally {
        setVerificandoMoodle(
          false
        );
      }
    };

  /* ==========================================================
     TOGGLE INDIVIDUAL

     Las existentes NO pueden seleccionarse.
     ========================================================== */

  const toggleMateria = (
    uid
  ) => {
    if (
      !moodleVerificado
    ) {
      return;
    }

    const estado =
      estadoMoodle[uid];

    if (
      estado?.existe === true
    ) {
      return;
    }

    if (
      estado?.estado ===
      "error"
    ) {
      return;
    }

    setSeleccionadas(
      (prev) => ({
        ...prev,

        [uid]:
          !prev[uid],
      })
    );
  };

  /* ==========================================================
     SELECCIONAR TODAS LAS NUEVAS
     ========================================================== */

  const seleccionarTodas =
    () => {
      if (
        !estructuraVista ||
        !moodleVerificado
      ) {
        return;
      }

      const nuevasSeleccionadas =
        {};

      estructuraVista.forEach(
        (division) => {
          division.materias.forEach(
            (materia) => {
              const estado =
                estadoMoodle[
                  materia.uid
                ];

              nuevasSeleccionadas[
                materia.uid
              ] =
                estado?.existe ===
                  false &&
                estado?.estado !==
                  "error";
            }
          );
        }
      );

      setSeleccionadas(
        nuevasSeleccionadas
      );
    };

  /* ==========================================================
     DESELECCIONAR TODAS
     ========================================================== */

  const deseleccionarTodas =
    () => {
      if (
        !estructuraVista
      ) {
        return;
      }

      const nuevasSeleccionadas =
        {};

      estructuraVista.forEach(
        (division) => {
          division.materias.forEach(
            (materia) => {
              nuevasSeleccionadas[
                materia.uid
              ] = false;
            }
          );
        }
      );

      setSeleccionadas(
        nuevasSeleccionadas
      );
    };

  /* ==========================================================
     VALIDAR SHORTNAMES
     ========================================================== */

  const validarShortnames =
    () => {
      const usados =
        new Map();

      for (
        const materia of
        materiasSeleccionadas
      ) {
        const shortname =
          String(
            materia.shortname ||
              ""
          ).trim();

        if (!shortname) {
          return {
            valido: false,

            titulo:
              "Shortname vacío",

            mensaje:
              `La materia "${materia.nombreMateria}" no tiene shortname.`,
          };
        }

        if (
          shortname.length >
          100
        ) {
          return {
            valido: false,

            titulo:
              "Shortname demasiado largo",

            mensaje:
              `El shortname "${shortname}" supera los 100 caracteres.`,
          };
        }

        if (
          !/^[A-Za-z0-9_-]+$/.test(
            shortname
          )
        ) {
          return {
            valido: false,

            titulo:
              "Shortname inválido",

            mensaje:
              `El shortname "${shortname}" solamente puede contener letras, números, guion y guion bajo.`,
          };
        }

        const shortnameNormalizado =
          shortname.toUpperCase();

        if (
          usados.has(
            shortnameNormalizado
          )
        ) {
          const materiaAnterior =
            usados.get(
              shortnameNormalizado
            );

          return {
            valido: false,

            titulo:
              "Shortname duplicado",

            mensaje:
              `El shortname "${shortname}" está repetido en "${materiaAnterior}" y "${materia.nombreMateria}".`,
          };
        }

        usados.set(
          shortnameNormalizado,
          materia.nombreMateria
        );
      }

      return {
        valido: true,
      };
    };

  /* ==========================================================
     CONFIRMACIÓN PREVIA
     ========================================================== */

  const confirmarCreacion =
    async () => {
      const filas =
        materiasSeleccionadas
          .map(
            (
              materia,
              index
            ) => `
              <tr>

                <td style="
                  border:1px solid #dee2e6;
                  padding:7px;
                  text-align:center;
                ">
                  ${index + 1}
                </td>

                <td style="
                  border:1px solid #dee2e6;
                  padding:7px;
                  text-align:center;
                ">
                  ${String(
                    materia.division
                  ).padStart(
                    2,
                    "0"
                  )}
                </td>

                <td style="
                  border:1px solid #dee2e6;
                  padding:7px;
                ">
                  ${escaparHTML(
                    materia.nombreMateria
                  )}
                </td>

                <td style="
                  border:1px solid #dee2e6;
                  padding:7px;
                ">
                  <code>
                    ${escaparHTML(
                      materia.shortname
                    )}
                  </code>
                </td>

                <td style="
                  border:1px solid #dee2e6;
                  padding:7px;
                  white-space:nowrap;
                ">
                  ${formatearFecha(
                    materia.startdate
                  )}
                </td>

                <td style="
                  border:1px solid #dee2e6;
                  padding:7px;
                  white-space:nowrap;
                ">
                  ${formatearFecha(
                    materia.enddate
                  )}
                </td>

              </tr>
            `
          )
          .join("");

      const resultado =
        await Swal.fire({
          icon: "question",

          title:
            `¿Crear ${materiasSeleccionadas.length} aula${
              materiasSeleccionadas.length ===
              1
                ? ""
                : "s"
            } nueva${
              materiasSeleccionadas.length ===
              1
                ? ""
                : "s"
            }?`,

          width: 1100,

          showCancelButton:
            true,

          confirmButtonText:
            "Sí, crear aulas nuevas",

          cancelButtonText:
            "Cancelar",

          confirmButtonColor:
            "#198754",

          cancelButtonColor:
            "#d33",

          reverseButtons:
            true,

          html: `
            <div style="text-align:left;">

              <div style="
                padding:12px;
                margin-bottom:15px;
                background:#d1e7dd;
                border:1px solid #badbcc;
                border-radius:8px;
              ">

                <strong>
                  🆕 Se crearán solamente las aulas nuevas.
                </strong>

                <br>

                Las aulas que Moodle informó como existentes
                no se enviarán para crear desde esta selección.

              </div>

              <div style="
                display:grid;
                grid-template-columns:repeat(2,minmax(0,1fr));
                gap:8px;
                padding:12px;
                margin-bottom:15px;
                background:#f8f9fa;
                border:1px solid #dee2e6;
                border-radius:8px;
              ">

                <div>
                  <strong>Curso:</strong><br>
                  ${escaparHTML(
                    cursoSeleccionado.nombreCurso
                  )}
                </div>

                <div>
                  <strong>ID:</strong><br>
                  ${escaparHTML(
                    cursoSeleccionado.id
                  )}
                </div>

                <div>
                  <strong>Categoría:</strong><br>
                  ${escaparHTML(
                    categoriaFinal?.nombre ||
                      categoriaFinal?.name ||
                      "Sin nombre"
                  )}
                </div>

                <div>
                  <strong>Aulas nuevas:</strong><br>
                  ${
                    materiasSeleccionadas.length
                  }
                </div>

              </div>

              <div style="
                max-height:420px;
                overflow:auto;
              ">

                <table style="
                  width:100%;
                  border-collapse:collapse;
                  font-size:13px;
                ">

                  <thead style="
                    position:sticky;
                    top:0;
                    background:#e9ecef;
                  ">
                    <tr>
                      <th style="border:1px solid #dee2e6;padding:7px;">#</th>
                      <th style="border:1px solid #dee2e6;padding:7px;">Div.</th>
                      <th style="border:1px solid #dee2e6;padding:7px;">Materia</th>
                      <th style="border:1px solid #dee2e6;padding:7px;">Shortname</th>
                      <th style="border:1px solid #dee2e6;padding:7px;">Inicio</th>
                      <th style="border:1px solid #dee2e6;padding:7px;">Fin</th>
                    </tr>
                  </thead>

                  <tbody>
                    ${filas}
                  </tbody>

                </table>

              </div>

            </div>
          `,
        });

      return resultado.isConfirmed;
    };

  /* ==========================================================
     MOSTRAR RESULTADO BACKEND
     ========================================================== */

  const mostrarResultadoBackend =
    async ({
      respuestaBack,
      categoriaCurso,
      categoriasDivisiones,
    }) => {
      const datos =
        respuestaBack?.data ??
        respuestaBack;

      const mensaje =
        obtenerMensajeBackend(
          datos
        );

      const resultados =
        datos?.resultados ||
        datos?.aulas ||
        datos?.cursos ||
        datos?.creados ||
        datos?.data ||
        [];

      let detalleResultado =
        "";

      if (
        Array.isArray(
          resultados
        ) &&
        resultados.length > 0
      ) {
        detalleResultado = `
          <div style="
            margin-top:15px;
            max-height:350px;
            overflow:auto;
          ">

            <table style="
              width:100%;
              border-collapse:collapse;
              font-size:13px;
            ">

              <thead style="
                position:sticky;
                top:0;
                background:#e9ecef;
              ">

                <tr>
                  <th style="border:1px solid #dee2e6;padding:7px;">#</th>
                  <th style="border:1px solid #dee2e6;padding:7px;">Aula</th>
                  <th style="border:1px solid #dee2e6;padding:7px;">ID Moodle</th>
                  <th style="border:1px solid #dee2e6;padding:7px;">Estado</th>
                </tr>

              </thead>

              <tbody>

                ${resultados
                  .map(
                    (
                      item,
                      index
                    ) => {
                      const nombre =
                        item?.shortname ||
                        item?.nombre ||
                        item?.fullname ||
                        item?.nombreMateria ||
                        item?.curso ||
                        "Sin nombre";

                      const id =
                        item?.idCursoMoodle ||
                        item?.courseid ||
                        item?.idMoodle ||
                        item?.moodleId ||
                        item?.id ||
                        "-";

                      const estado =
                        item?.mensaje ||
                        item?.estado ||
                        item?.status ||
                        item?.resultado ||
                        "Procesado";

                      return `
                        <tr>

                          <td style="
                            border:1px solid #dee2e6;
                            padding:7px;
                            text-align:center;
                          ">
                            ${index + 1}
                          </td>

                          <td style="
                            border:1px solid #dee2e6;
                            padding:7px;
                          ">
                            ${escaparHTML(
                              nombre
                            )}
                          </td>

                          <td style="
                            border:1px solid #dee2e6;
                            padding:7px;
                          ">
                            ${escaparHTML(
                              id
                            )}
                          </td>

                          <td style="
                            border:1px solid #dee2e6;
                            padding:7px;
                          ">
                            ${escaparHTML(
                              estado
                            )}
                          </td>

                        </tr>
                      `;
                    }
                  )
                  .join("")}

              </tbody>

            </table>

          </div>
        `;
      }

      const divisionesHTML =
        categoriasDivisiones
          .map(
            (
              division
            ) => `
              <li>
                ${escaparHTML(
                  division.nombre
                )}
                — ID
                ${escaparHTML(
                  division.id
                )}

                ${
                  division.creada
                    ? "<strong>(creada)</strong>"
                    : "(ya existía)"
                }
              </li>
            `
          )
          .join("");

      await Swal.fire({
        icon:
          datos?.ok === false
            ? "warning"
            : "success",

        title:
          datos?.ok === false
            ? "Proceso terminado con observaciones"
            : "Aulas procesadas",

        width: 950,

        confirmButtonText:
          "Aceptar",

        html: `
          <div style="text-align:left;">

            <p style="font-size:16px;">
              ${escaparHTML(
                mensaje
              )}
            </p>

            <div style="
              display:grid;
              grid-template-columns:repeat(3,1fr);
              gap:8px;
              margin-bottom:15px;
            ">

              <div style="
                padding:10px;
                background:#d1e7dd;
                border-radius:8px;
                text-align:center;
              ">
                <strong>
                  🆕 Creadas
                </strong>
                <br>
                ${
                  datos?.totalCreados ??
                  0
                }
              </div>

              <div style="
                padding:10px;
                background:#e7f1ff;
                border-radius:8px;
                text-align:center;
              ">
                <strong>
                  ♻️ Existentes
                </strong>
                <br>
                ${
                  datos?.totalExistentes ??
                  0
                }
              </div>

              <div style="
                padding:10px;
                background:#f8d7da;
                border-radius:8px;
                text-align:center;
              ">
                <strong>
                  ❌ Errores
                </strong>
                <br>
                ${
                  datos?.totalErrores ??
                  0
                }
              </div>

            </div>

            <div style="
              padding:12px;
              background:#f8f9fa;
              border:1px solid #dee2e6;
              border-radius:8px;
            ">

              <strong>
                Categoría del curso:
              </strong>

              ${escaparHTML(
                categoriaCurso.nombre
              )}

              — ID

              ${escaparHTML(
                categoriaCurso.id
              )}

              ${
                categoriaCurso.creada
                  ? "<strong>(creada)</strong>"
                  : "(ya existía)"
              }

              <hr>

              <strong>
                Categorías de divisiones:
              </strong>

              <ul style="margin-top:8px;">
                ${divisionesHTML}
              </ul>

            </div>

            ${detalleResultado}

          </div>
        `,
      });
    };

  /* ==========================================================
     GENERAR AULAS NUEVAS
     ========================================================== */

  const handleGenerar =
    async () => {
      if (generando) {
        return;
      }

      if (
        !cursoSeleccionado
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "Seleccioná un curso",
        });

        return;
      }

      if (
        !categoriaFinal?.id
      ) {
        Swal.fire({
          icon: "warning",
          title:
            "Seleccioná una categoría",
        });

        return;
      }

      /*
       * SEGURIDAD:
       * no permitimos crear sin
       * haber verificado Moodle.
       */

      if (
        !moodleVerificado
      ) {
        Swal.fire({
          icon: "warning",

          title:
            "Primero verificá Moodle",

          text:
            "Antes de crear aulas tenés que verificar cuáles ya existen.",
        });

        return;
      }

      if (
        materiasSeleccionadas.length ===
        0
      ) {
        Swal.fire({
          icon: "info",

          title:
            "No hay aulas nuevas seleccionadas",

          text:
            "Las aulas existentes no necesitan volver a crearse.",
        });

        return;
      }

      const validacion =
        validarShortnames();

      if (
        !validacion.valido
      ) {
        Swal.fire({
          icon: "error",

          title:
            validacion.titulo,

          text:
            validacion.mensaje,
        });

        return;
      }

      const confirmado =
        await confirmarCreacion();

      if (!confirmado) {
        return;
      }

      setGenerando(true);
      setUltimoResultado(
        null
      );

      Swal.fire({
        title:
          "Creando aulas nuevas",

        html: `
          <p>
            Se están procesando solamente
            las aulas seleccionadas como nuevas.
          </p>

          <p>
            <strong>
              Las aulas existentes no serán modificadas.
            </strong>
          </p>
        `,

        allowOutsideClick:
          false,

        allowEscapeKey:
          false,

        showConfirmButton:
          false,

        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        /* ======================================================
           1. TRAER CATEGORÍAS MOODLE
           ====================================================== */

        const respCategorias =
          await axios.post(
            "https://wwwp.isepsantafe.net.ar/moodleservice/categorias?plataforma=cursos"
          );

        const categorias =
          Array.isArray(
            respCategorias.data
              ?.categorias
          )
            ? respCategorias.data
                .categorias
            : [];

        /* ======================================================
           2. CATEGORÍA DEL CURSO
           ====================================================== */

        const nombreCategoriaCurso =
          cursoSeleccionado.nombreCurso;

        let categoriaCurso =
          categorias.find(
            (categoria) =>
              categoria.nombre ===
                nombreCategoriaCurso &&
              Number(
                categoria.parent
              ) ===
                Number(
                  categoriaFinal.id
                )
          );

        let idCategoriaCurso;

        let categoriaCursoCreada =
          false;

        if (categoriaCurso) {
          idCategoriaCurso =
            categoriaCurso.id;
        } else {
          const respCrearCurso =
            await axios.post(
              "https://wwwp.isepsantafe.net.ar/moodleservice/categorias/crear",
              null,
              {
                params: {
                  plataforma:
                    "cursos",

                  parent:
                    categoriaFinal.id,

                  name:
                    nombreCategoriaCurso,
                },
              }
            );

          idCategoriaCurso =
            respCrearCurso.data
              ?.categoria?.id;

          categoriaCurso =
            respCrearCurso.data
              ?.categoria;

          categoriaCursoCreada =
            true;

          if (
            !idCategoriaCurso
          ) {
            throw new Error(
              "No se pudo crear la categoría del curso."
            );
          }
        }

        /* ======================================================
           3. DIVISIONES
           ====================================================== */

        const estructuraConCategorias =
          [];

        const categoriasDivisiones =
          [];

        const categoriasDisponibles =
          [...categorias];

        if (
          categoriaCursoCreada &&
          categoriaCurso
        ) {
          categoriasDisponibles.push(
            categoriaCurso
          );
        }

        for (
          const division of
          estructuraVista
        ) {
          /*
           * SOLAMENTE materias
           * NUEVAS seleccionadas.
           */

          const materiasDivision =
            division.materias.filter(
              (materia) =>
                seleccionadas[
                  materia.uid
                ] &&
                estadoMoodle[
                  materia.uid
                ]?.existe ===
                  false &&
                estadoMoodle[
                  materia.uid
                ]?.estado !==
                  "error"
            );

          if (
            materiasDivision.length ===
            0
          ) {
            continue;
          }

          const nombreDivision =
            `Division ${String(
              division.division
            ).padStart(
              2,
              "0"
            )}`;

          let categoriaDivision =
            categoriasDisponibles.find(
              (categoria) =>
                categoria.nombre ===
                  nombreDivision &&
                Number(
                  categoria.parent
                ) ===
                  Number(
                    idCategoriaCurso
                  )
            );

          let idCategoriaDivision;

          let categoriaDivisionCreada =
            false;

          if (
            categoriaDivision
          ) {
            idCategoriaDivision =
              categoriaDivision.id;
          } else {
            const respCrearDivision =
              await axios.post(
                "https://wwwp.isepsantafe.net.ar/moodleservice/categorias/crear",
                null,
                {
                  params: {
                    plataforma:
                      "cursos",

                    parent:
                      idCategoriaCurso,

                    name:
                      nombreDivision,
                  },
                }
              );

            categoriaDivision =
              respCrearDivision.data
                ?.categoria;

            idCategoriaDivision =
              categoriaDivision?.id;

            categoriaDivisionCreada =
              true;

            if (
              !idCategoriaDivision
            ) {
              throw new Error(
                `No se pudo crear la categoría ${nombreDivision}.`
              );
            }

            categoriasDisponibles.push(
              categoriaDivision
            );
          }

          categoriasDivisiones.push({
            id:
              idCategoriaDivision,

            nombre:
              nombreDivision,

            creada:
              categoriaDivisionCreada,
          });

          estructuraConCategorias.push({
            division:
              division.division,

            idCategoria:
              idCategoriaDivision,

            nombreCategoria:
              nombreDivision,

            materias:
              materiasDivision.map(
                (materia) => ({
                  ...materia,

                  shortname:
                    shortnamesEditados[
                      materia.uid
                    ] ??
                    materia.shortname,

                  idCategoria:
                    idCategoriaDivision,

                  idCurso:
                    cursoSeleccionado.id,

                  nombreCurso:
                    cursoSeleccionado.nombreCurso,
                })
              ),
          });
        }

        if (
          estructuraConCategorias.length ===
          0
        ) {
          throw new Error(
            "No quedaron aulas nuevas para enviar al backend."
          );
        }

        /* ======================================================
           4. PAYLOAD
           ====================================================== */

        const payloadBack = {
          curso:
            cursoSeleccionado.nombreCurso,

          idCurso:
            cursoSeleccionado.id,

          categoriaPadre:
            categoriaFinal,

          categoriaCurso: {
            id:
              idCategoriaCurso,

            nombre:
              nombreCategoriaCurso,

            creada:
              categoriaCursoCreada,
          },

          cantidadAulas:
            materiasSeleccionadas.length,

          estructuraConCategorias,
        };

        /* ======================================================
           5. BACKEND

           El backend vuelve a comprobar
           que cada aula no exista.
           ====================================================== */

        const respuestaBack =
          await axios.post(
            "https://wwwa.isepsantafe.net.ar/expedientes/moodles/recibirEstructuraMoodle",
            payloadBack
          );

        setUltimoResultado(
          respuestaBack.data
        );

        Swal.close();

        await mostrarResultadoBackend({
          respuestaBack,

          categoriaCurso: {
            id:
              idCategoriaCurso,

            nombre:
              nombreCategoriaCurso,

            creada:
              categoriaCursoCreada,
          },

          categoriasDivisiones,
        });

        /*
         * Después de crear,
         * volvemos a verificar.
         *
         * Así las recién creadas
         * aparecerán como existentes.
         */

        resetearVerificacionMoodle();

        setSeleccionadas({});
      } catch (error) {
        console.error(
          "Error completo al generar Moodle:",
          error
        );

        Swal.close();

        const informacionBackend =
          error?.response?.data;

        const mensajeError =
          informacionBackend
            ?.mensaje ||
          informacionBackend
            ?.message ||
          informacionBackend
            ?.error ||
          error?.message ||
          "Ocurrió un problema al generar la estructura Moodle.";

        Swal.fire({
          icon: "error",

          title:
            "No se pudieron crear las aulas",

          width: 850,

          confirmButtonText:
            "Aceptar",

          html: `
            <div style="text-align:left;">

              <p>
                ${escaparHTML(
                  mensajeError
                )}
              </p>

              ${
                informacionBackend
                  ? `
                    <details style="
                      margin-top:15px;
                      border:1px solid #dee2e6;
                      border-radius:8px;
                      padding:10px;
                      background:#f8f9fa;
                    ">

                      <summary style="
                        cursor:pointer;
                        font-weight:bold;
                      ">
                        Ver información completa del backend
                      </summary>

                      <pre style="
                        margin-top:10px;
                        max-height:350px;
                        overflow:auto;
                        white-space:pre-wrap;
                        overflow-wrap:anywhere;
                        font-size:12px;
                      ">${escaparHTML(
                        JSON.stringify(
                          informacionBackend,
                          null,
                          2
                        )
                      )}</pre>

                    </details>
                  `
                  : ""
              }

            </div>
          `,
        });
      } finally {
        setGenerando(false);
      }
    };

  /* ==========================================================
     CAMBIO DE CATEGORÍA

     Si cambia la categoría,
     la verificación anterior deja de ser válida.
     ========================================================== */

  const manejarCambioCategoria =
    (categoria) => {
      setCategoriaFinal(
        categoria
      );

      setSeleccionadas(
        {}
      );

      setEstadoMoodle(
        {}
      );

      setMoodleVerificado(
        false
      );

      setUltimoResultado(
        null
      );

      inicializadoRef.current =
        false;
    };

  /* ==========================================================
     RENDER
     ========================================================== */

  return (
    <>
      {/* ======================================================
          SELECCIÓN DE CURSO
          ====================================================== */}

      <div className="form-floating mb-3">
        <select
          className="form-select"
          value={
            cursoSeleccionado?.id ||
            ""
          }
          disabled={
            generando ||
            verificandoMoodle
          }
          onChange={(
            event
          ) => {
            const idCurso =
              event.target.value;

            const curso =
              cursosValidos.find(
                (item) =>
                  String(
                    item.id
                  ) ===
                  String(
                    idCurso
                  )
              );

            setCursoSeleccionado(
              curso || null
            );

            setCategoriaFinal(
              null
            );

            setMaterias([]);

            setSeleccionadas(
              {}
            );

            setShortnamesEditados(
              {}
            );

            setEditandoUid(
              null
            );

            setUltimoResultado(
              null
            );

            setEstadoMoodle(
              {}
            );

            setMoodleVerificado(
              false
            );

            inicializadoRef.current =
              false;
          }}
        >
          <option value="">
            Seleccionar curso
          </option>

          {cursosValidos.map(
            (curso) => (
              <option
                key={curso.id}
                value={curso.id}
              >
                {
                  curso.nombreCurso
                }{" "}
                — ID {curso.id}
              </option>
            )
          )}
        </select>

        <label>
          Curso
        </label>
      </div>

      {/* ======================================================
          DATOS DEL CURSO
          ====================================================== */}

      {cursoSeleccionado && (
        <div className="alert alert-light border mb-3">

          <div>
            <strong>
              Curso:
            </strong>{" "}
            {
              cursoSeleccionado.nombreCurso
            }
          </div>

          <div>
            <strong>
              ID:
            </strong>{" "}
            {
              cursoSeleccionado.id
            }
          </div>

          <div>
            <strong>
              Divisiones:
            </strong>{" "}
            {
              cursoSeleccionado.cantDivisiones ||
              1
            }
          </div>

        </div>
      )}

      {/* ======================================================
          CATEGORÍA
          ====================================================== */}

      {cursoSeleccionado && (
        <SelectorCategoriaMoodle
          plataforma="cursos"
          onChange={
            manejarCambioCategoria
          }
        />
      )}

      {/* ======================================================
          CARGANDO MATERIAS
          ====================================================== */}

      {cargandoMaterias && (
        <div className="alert alert-info text-center">

          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
          />

          Cargando materias del curso...

        </div>
      )}

      {/* ======================================================
          BOTÓN VERIFICAR MOODLE
          ====================================================== */}

      {categoriaFinal &&
        estructuraVista && (
          <div className="card border-primary mb-3">

            <div className="card-body">

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">

                <div>
                  <h5 className="card-title mb-1">
                    🔎 Verificación Moodle
                  </h5>

                  <div className="text-muted">
                    Consultá qué aulas ya existen antes de crear.
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    verificandoMoodle ||
                    generando
                  }
                  onClick={
                    verificarAulasMoodle
                  }
                >

                  {verificandoMoodle ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />

                      Verificando...
                    </>
                  ) : moodleVerificado ? (
                    <>
                      🔄 Verificar nuevamente
                    </>
                  ) : (
                    <>
                      🔎 Verificar aulas existentes
                    </>
                  )}

                </button>

              </div>

            </div>

          </div>
        )}

      {/* ======================================================
          RESUMEN DE VERIFICACIÓN
          ====================================================== */}

      {moodleVerificado && (
        <div className="row g-2 mb-3">

          <div className="col-md-4">
            <div className="alert alert-primary mb-0 text-center">
              <strong>
                ♻️ Ya existentes
              </strong>

              <div
                style={{
                  fontSize:
                    "24px",
                }}
              >
                {
                  resumenMoodle.existentes
                }
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="alert alert-success mb-0 text-center">
              <strong>
                🆕 Nuevas
              </strong>

              <div
                style={{
                  fontSize:
                    "24px",
                }}
              >
                {
                  resumenMoodle.nuevos
                }
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="alert alert-danger mb-0 text-center">
              <strong>
                ❌ Errores
              </strong>

              <div
                style={{
                  fontSize:
                    "24px",
                }}
              >
                {
                  resumenMoodle.errores
                }
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ======================================================
          ACCIONES DE SELECCIÓN
          ====================================================== */}

      {estructuraVista && (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">

          <div>
            <strong>
              Aulas nuevas seleccionadas:
            </strong>{" "}

            {
              materiasSeleccionadas.length
            }
          </div>

          {moodleVerificado && (
            <div className="d-flex gap-2">

              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                disabled={
                  generando
                }
                onClick={
                  seleccionarTodas
                }
              >
                Seleccionar todas las nuevas
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled={
                  generando
                }
                onClick={
                  deseleccionarTodas
                }
              >
                Deseleccionar
              </button>

            </div>
          )}

        </div>
      )}

      {/* ======================================================
          DIVISIONES / MATERIAS
          ====================================================== */}

      {estructuraVista?.map(
        (division) => (
          <div
            key={
              division.division
            }
            className="mb-3 p-2 border rounded"
          >

            <h6>
              📘 División{" "}
              {String(
                division.division
              ).padStart(
                2,
                "0"
              )}
            </h6>

            <ul className="list-group">

              {division.materias.map(
                (materia) => {
                  const estado =
                    estadoMoodle[
                      materia.uid
                    ];

                  const existe =
                    estado?.existe ===
                    true;

                  const esNuevo =
                    moodleVerificado &&
                    estado?.existe ===
                      false &&
                    estado?.estado !==
                      "error";

                  const tieneError =
                    estado?.estado ===
                    "error";

                  const estaSeleccionada =
                    !!seleccionadas[
                      materia.uid
                    ];

                  const shortnameActual =
                    shortnamesEditados[
                      materia.uid
                    ] ??
                    materia.shortname;

                  let claseEstado =
                    "";

                  if (existe) {
                    claseEstado =
                      "list-group-item-secondary";
                  }

                  if (esNuevo) {
                    claseEstado =
                      "list-group-item-success";
                  }

                  if (
                    tieneError
                  ) {
                    claseEstado =
                      "list-group-item-danger";
                  }

                  return (
                    <li
                      key={
                        materia.uid
                      }
                      className={`list-group-item ${claseEstado}`}
                    >

                      <div className="row align-items-center g-2">

                        {/* =====================================
                            CHECK + NOMBRE
                            ===================================== */}

                        <div className="col-md-5">

                          <div className="form-check">

                            <input
                              id={`check-${materia.uid}`}
                              className="form-check-input"
                              type="checkbox"
                              checked={
                                estaSeleccionada
                              }
                              disabled={
                                generando ||
                                verificandoMoodle ||
                                !moodleVerificado ||
                                existe ||
                                tieneError
                              }
                              onChange={() =>
                                toggleMateria(
                                  materia.uid
                                )
                              }
                            />

                            <label
                              className="form-check-label"
                              htmlFor={`check-${materia.uid}`}
                            >
                              <strong>
                                {
                                  materia.nombreMateria
                                }
                              </strong>
                            </label>

                          </div>

                          {/* =================================
                              ESTADO MOODLE
                              ================================= */}

                          {!moodleVerificado && (
                            <small className="text-muted">
                              ⏳ Pendiente de verificar
                            </small>
                          )}

                          {existe && (
                            <div className="mt-1">

                              <span className="badge bg-secondary">
                                ♻️ YA EXISTE
                              </span>

                              {estado?.idCursoMoodle && (
                                <span className="ms-2 text-muted small">
                                  Moodle ID{" "}
                                  {
                                    estado.idCursoMoodle
                                  }
                                </span>
                              )}

                            </div>
                          )}

                          {esNuevo && (
                            <div className="mt-1">

                              <span className="badge bg-success">
                                🆕 NUEVA
                              </span>

                              <span className="ms-2 small">
                                Se puede crear
                              </span>

                            </div>
                          )}

                          {tieneError && (
                            <div className="mt-1">

                              <span className="badge bg-danger">
                                ❌ ERROR
                              </span>

                              <div className="small text-danger mt-1">
                                {
                                  estado?.error ||
                                  "No se pudo verificar."
                                }
                              </div>

                            </div>
                          )}

                        </div>

                        {/* =====================================
                            SHORTNAME
                            ===================================== */}

                        <div className="col-md-7">

                          {editandoUid ===
                            materia.uid &&
                          !existe &&
                          !moodleVerificado ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={
                                shortnameActual
                              }
                              autoFocus
                              disabled={
                                generando
                              }
                              onChange={(
                                event
                              ) => {
                                setShortnamesEditados(
                                  (
                                    prev
                                  ) => ({
                                    ...prev,

                                    [materia.uid]:
                                      event.target.value
                                        .toUpperCase()
                                        .replace(
                                          /\s+/g,
                                          "_"
                                        ),
                                  })
                                );

                                /*
                                 * Si cambia el shortname,
                                 * cualquier verificación previa
                                 * deja de ser válida.
                                 */

                                resetearVerificacionMoodle();
                              }}
                              onBlur={() =>
                                setEditandoUid(
                                  null
                                )
                              }
                              onKeyDown={(
                                event
                              ) => {
                                if (
                                  event.key ===
                                  "Enter"
                                ) {
                                  setEditandoUid(
                                    null
                                  );
                                }

                                if (
                                  event.key ===
                                  "Escape"
                                ) {
                                  setShortnamesEditados(
                                    (
                                      prev
                                    ) => {
                                      const copia =
                                        {
                                          ...prev,
                                        };

                                      delete copia[
                                        materia.uid
                                      ];

                                      return copia;
                                    }
                                  );

                                  setEditandoUid(
                                    null
                                  );

                                  resetearVerificacionMoodle();
                                }
                              }}
                            />
                          ) : (
                            <div className="d-flex flex-column align-items-md-end">

                              <code
                                style={{
                                  cursor:
                                    !moodleVerificado &&
                                    !generando
                                      ? "pointer"
                                      : "default",

                                  overflowWrap:
                                    "anywhere",
                                }}
                                onDoubleClick={() => {
                                  /*
                                   * Sólo permitimos cambiar
                                   * shortname antes de verificar.
                                   */

                                  if (
                                    !generando &&
                                    !moodleVerificado
                                  ) {
                                    setEditandoUid(
                                      materia.uid
                                    );
                                  }
                                }}
                                title={
                                  moodleVerificado
                                    ? "Para editar el shortname verificá nuevamente después"
                                    : "Doble clic para editar"
                                }
                              >
                                {
                                  shortnameActual
                                }
                              </code>

                              {existe &&
                                estado?.shortnameMoodle &&
                                estado.shortnameMoodle !==
                                  shortnameActual && (
                                  <small className="text-muted mt-1">
                                    Moodle:{" "}
                                    {
                                      estado.shortnameMoodle
                                    }
                                  </small>
                                )}

                            </div>
                          )}

                        </div>

                      </div>

                    </li>
                  );
                }
              )}

            </ul>

          </div>
        )
      )}

      {/* ======================================================
          SIN MATERIAS
          ====================================================== */}

      {!cargandoMaterias &&
        cursoSeleccionado &&
        materias.length === 0 && (
          <div className="alert alert-warning">
            El curso seleccionado no tiene materias.
          </div>
        )}

      {/* ======================================================
          AVISO ANTES DE VERIFICAR
          ====================================================== */}

      {categoriaFinal &&
        estructuraVista &&
        !moodleVerificado && (
          <div className="alert alert-info">

            <strong>
              ℹ️ Antes de crear:
            </strong>

            <br />

            Presioná{" "}
            <strong>
              "Verificar aulas existentes"
            </strong>{" "}
            para comparar esta estructura con Moodle.

            <br />

            <strong>
              Esta verificación no crea ni modifica nada.
            </strong>

          </div>
        )}

      {/* ======================================================
          TODO YA EXISTE
          ====================================================== */}

      {moodleVerificado &&
        resumenMoodle.nuevos ===
          0 &&
        resumenMoodle.errores ===
          0 && (
          <div className="alert alert-success">

            <strong>
              ✅ Moodle ya está actualizado.
            </strong>

            <br />

            Todas las aulas de este curso ya existen.
            No hay nada nuevo para crear.

          </div>
        )}

      {/* ======================================================
          BOTÓN CREAR
          ====================================================== */}

      {categoriaFinal &&
        estructuraVista &&
        moodleVerificado &&
        resumenMoodle.nuevos >
          0 && (
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4">

            <div>

              <div>
                <strong>
                  🆕 Aulas nuevas encontradas:
                </strong>{" "}

                {
                  resumenMoodle.nuevos
                }
              </div>

              <div className="text-muted small">
                Solamente se enviarán las que estén seleccionadas.
              </div>

            </div>

            <button
              type="button"
              className="btn btn-success btn-lg"
              disabled={
                generando ||
                verificandoMoodle ||
                materiasSeleccionadas.length ===
                  0
              }
              onClick={
                handleGenerar
              }
            >

              {generando ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />

                  Creando...
                </>
              ) : (
                <>
                  🆕 Crear{" "}
                  {
                    materiasSeleccionadas.length
                  }{" "}
                  aula
                  {materiasSeleccionadas.length ===
                  1
                    ? ""
                    : "s"}{" "}
                  nueva
                  {materiasSeleccionadas.length ===
                  1
                    ? ""
                    : "s"}
                </>
              )}

            </button>

          </div>
        )}

      {/* ======================================================
          ÚLTIMA RESPUESTA
          ====================================================== */}

      {ultimoResultado && (
        <details className="mt-3 border rounded p-3 bg-light">

          <summary
            style={{
              cursor:
                "pointer",

              fontWeight:
                "bold",
            }}
          >
            Ver la última respuesta completa del backend
          </summary>

          <pre
            className="mt-3 mb-0"
            style={{
              maxHeight:
                "400px",

              overflow:
                "auto",

              whiteSpace:
                "pre-wrap",

              overflowWrap:
                "anywhere",

              fontSize:
                "12px",
            }}
          >
            {JSON.stringify(
              ultimoResultado,
              null,
              2
            )}
          </pre>

        </details>
      )}
    </>
  );
}

export default SelectorCursoMateria;